import React from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import ThreeHero from './ThreeHero.jsx';
import Avatar from '../images/mevr.png';
import Cv from '../images/cv.pdf';

const Section = styled.div`
  padding: 90px 100px 70px 100px;
  position: relative;
  z-index: 2;
  display: flex;
  gap: 40px;
  align-items: flex-start;
  @media (max-width: 900px) {
    flex-direction: column;
    padding: 50px 24px 40px 24px;
  }
`;
const GhostMark = styled.div`
  position: absolute;
  top: -40px;
  right: 60px;
  font-family: ${theme.fontDisplay};
  font-size: clamp(140px, 24vw, 340px);
  line-height: 1;
  color: transparent;
  -webkit-text-stroke: 1.5px rgba(245,245,242,0.06);
  letter-spacing: -0.02em;
  z-index: 0;
  pointer-events: none;
  user-select: none;
  animation: driftSlow 9s ease-in-out infinite;
`;
const Copy = styled.div`
  flex: 1.15;
  position: relative;
  z-index: 2;
`;
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;
const Badge = styled.div`
  display: inline-block;
  font-family: ${theme.fontMono};
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${theme.accent};
  color: ${theme.bg};
  padding: 8px 16px;
  animation: fadeUp 0.6s ease both;
`;
const Available = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${theme.fontMono};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  border: 1px solid ${theme.borderLight};
  padding: 8px 14px;
  animation: fadeUp 0.6s ease both 0.1s;
`;
const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.green};
  display: inline-block;
  animation: pulse 1.6s ease-in-out infinite;
`;
const H1 = styled.h1`
  margin: 0 0 8px 0;
  font-family: ${theme.fontDisplay};
  font-size: clamp(46px, 6.4vw, 92px);
  line-height: 0.94;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  animation: fadeUp 0.7s ease both 0.15s;
`;
const Cursor = styled.span`
  display: inline-block;
  width: 10px;
  height: 0.72em;
  background: ${theme.accent};
  margin-left: 10px;
  vertical-align: -0.08em;
  animation: blink 1s step-end infinite;
`;
const Sub = styled.p`
  margin: 24px 0 36px 0;
  font-size: 19px;
  line-height: 1.6;
  color: ${theme.textMuted};
  max-width: 540px;
  font-weight: 400;
  animation: fadeUp 0.7s ease both 0.25s;
`;
const CtaRow = styled.div`
  display: flex;
  gap: 16px;
  animation: fadeUp 0.7s ease both 0.35s;
  flex-wrap: wrap;
`;
const Primary = styled.a`
  padding: 18px 30px;
  background: ${theme.accent};
  color: ${theme.bg} !important;
  font-family: ${theme.fontHeading};
  font-weight: 800;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  box-shadow: 6px 6px 0 ${theme.text};
  cursor: pointer;
`;
const Secondary = styled.a`
  padding: 18px 30px;
  border: 2px solid #3A3A42;
  color: ${theme.text} !important;
  font-family: ${theme.fontHeading};
  font-weight: 800;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
const Visual = styled.div`
  flex: 1;
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  min-height: 520px;
  width: 100%;
  @media (max-width: 900px) {
    min-height: 360px;
  }
`;
const FillBox = styled.div`
  position: absolute;
  top: 20px;
  right: 10px;
  width: 340px;
  height: 340px;
  background: ${theme.accent};
  z-index: 1;
  animation: drift 6s ease-in-out infinite;
  @media (max-width: 900px) {
    width: 240px;
    height: 240px;
  }
`;
const ThreeLayer = styled.div`
  position: absolute;
  inset: -60px;
  z-index: 1;
  pointer-events: none;
`;
const AvatarImg = styled.img`
  position: relative;
  z-index: 2;
  width: 560px;
  max-width: 90%;
  height: auto;
  display: block;
  margin-bottom: -70px;
  filter: drop-shadow(0 14px 18px rgba(0,0,0,0.4));
  @media (max-width: 900px) {
    width: 420px;
    max-width: 85%;
    margin-bottom: -40px;
  }
`;
const ScrollCue = styled.div`
  position: absolute;
  bottom: 0px;
  left: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  z-index: 2;
  @media (max-width: 900px) {
    display: none;
  }
`;
const ScrollLabel = styled.div`
  font-family: ${theme.fontMono};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.textDim};
`;
const Chevron = styled.div`
  animation: bounce 1.6s ease-in-out infinite;
`;

export default function Hero() {
  return (
    <Section id="Home">
      <GhostMark>ZR</GhostMark>
      <Copy>
        <BadgeRow>
          <Badge>Full-Stack &middot; MERN &middot; Cape Town</Badge>
          <Available><Dot />Available for work</Available>
        </BadgeRow>
        <H1>Zaheer<br />Roberts<Cursor /></H1>
        <Sub>
          I build fast, reliable products end to end. Currently at Full Stack (Pty) Ltd &mdash;
          recent client work spans Virgin Active, Travelstart, Ooba Home Loans and Buyers Trust.
        </Sub>
        <CtaRow>
          <Primary onClick={() => document.getElementById('Work')?.scrollIntoView({ behavior: 'smooth' })}>View My Work</Primary>
          <Secondary href={Cv} target="_blank" rel="noopener noreferrer">Download CV</Secondary>
        </CtaRow>
      </Copy>
      <Visual>
        <ThreeLayer>
          <ThreeHero color={theme.accent} />
        </ThreeLayer>
        <FillBox />
        <AvatarImg src={Avatar} alt="Zaheer Roberts" />
      </Visual>
      <ScrollCue>
        <ScrollLabel>Scroll</ScrollLabel>
        <Chevron>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6C6C75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </Chevron>
      </ScrollCue>
    </Section>
  );
}
